'use client';

import { ReactNode, Suspense, lazy } from 'react';
import { type LottieComponentProps } from 'lottie-react';
import { motion } from 'motion/react';

import { useQuery } from '@northladder/services';

interface IEnterAnimationProps {
  children: ReactNode;
}

function EnterAnimation({ children }: IEnterAnimationProps) {
  return (
    <motion.div
      animate={{ opacity: 1, scale: 1 }}
      initial={{ opacity: 0.5, scale: 0.5 }}
      transition={{
        duration: 0.2,
        scale: { type: 'spring', visualDuration: 0.2, bounce: 0.75 },
      }}
    >
      {children}
    </motion.div>
  );
}

const LazyLottieComponent = lazy(() => import('lottie-react'));

interface LottieProps<T extends Record<string, unknown>> {
  getAnimationData: () => Promise<T>;
  id: string;
}

/**
 * This hack will unfreeze the loaded animation data so it can later be
 * modified on by lottie in the browser env.
 */
function unFreezeAnimationData(data: Record<string, unknown>) {
  let animData = data;

  try {
    /**
     */
    animData = JSON.parse(JSON.stringify(animData));
  } catch (error) {
    // Failed to parse the animation data. Might be an invalid file.
    // eslint-disable-next-line no-console
    console.error({
      error,
      message: 'Failed to parse the animation data.',
    });
  }

  return animData;
}

export interface ISkeletonProps {
  height?: number | string;
  width?: number | string;
}

/**
 * -----------------------------------------------------------------------------
 * Lazy loading of the lottie animation component, wrapping around the base
 * React Lottie component, making sure, it's only lazy loaded in a browser env
 * and caching the animation data with RQ.
 */
export function LazyLottie<T extends Record<string, unknown>>({
  getAnimationData,
  id,
  ref,
  loop = true,
  height = 120,
  width = 120,
  ...props
}: LottieProps<T> & Omit<LottieComponentProps, 'animationData'>) {
  const { data } = useQuery({
    queryKey: [id],
    queryFn: async () => {
      import('lottie-react'); // Trigger the library lazy load even if the animationData is not ready
      return getAnimationData();
    },
    enabled: typeof window !== 'undefined',
    networkMode: 'always',
    staleTime: Infinity,
  });

  if (!data) return null;

  const newData = unFreezeAnimationData(data);

  return (
    <Suspense fallback={null}>
      <EnterAnimation>
        <LazyLottieComponent
          animationData={newData}
          {...props}
          height={height}
          loop={loop}
          width={width}
        />
      </EnterAnimation>
    </Suspense>
  );
}
